import React, { useState, useEffect } from "react";
import { Card, Table, Input, Button, Tag, Space, Tooltip, Select } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet, usePost, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const UserManager = () => {
  const { t, i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    mobile_number: "", // Sirf number without country code
    country_code: "+31", // Default country code for Netherlands
    user_type: "INDIVIDUAL",
    is_active: true,
    club: "",
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({});
  const [clubsList, setClubsList] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedStatusFilter = useDebounce(statusFilter, 500);
  const debouncedUserTypeFilter = useDebounce(userTypeFilter, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [userList, setUserList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const USER_TYPE_OPTIONS = [
    { value: "INDIVIDUAL", label: t("userManager.individual") },
    { value: "CLUB_COACH", label: t("userManager.clubCoach") },
    { value: "CLUB_ADMIN", label: t("userManager.clubAdmin") },
    { value: "KNKV_PARTICIPANT", label: t("userManager.knkvParticipant") },
    { value: "KNKV_INSTRUCTOR", label: t("userManager.knkvInstructor") },
    { value: "SYSTEM_ADMIN", label: t("userManager.systemAdmin") },
  ];

  const CLUB_REQUIRED_USER_TYPES = [
    "CLUB_ADMIN",
    "CLUB_COACH",
    // "INDIVIDUAL",
    "KNKV_INSTRUCTOR",
    "KNKV_PARTICIPANT",
  ];

  // Status filter options
  const STATUS_OPTIONS = [
    { value: "active", label: t("common.active") },
    { value: "inactive", label: t("common.inactive") },
  ];

  // API hooks
  const { execute: fetchList } = useGet(
    `${API_ENDPOINTS.USERS_LIST}?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearchTerm}&status=${statusFilter || ""}&user_type=${userTypeFilter || ""}`,
  );
  const { post } = usePost(API_ENDPOINTS.USERS_LIST);
  const { execute: getById } = useDynamicApi("GET");
  const { execute: putData } = useDynamicApi("PATCH");
  const { execute: patchData } = useDynamicApi("PATCH");
  const { execute: deleteData } = useDynamicApi("DELETE");
  const { execute: getClubs } = useDynamicApi("GET");

  // Load clubs list
  const loadClubs = async () => {
    setLoadingClubs(true);
    try {
      const response = await getClubs(
        API_ENDPOINTS.CLUBS_LIST_WITHOUT_PAGINATE,
      );
      if (response?.status === true && response.data) {
        setClubsList(response.data);
      } else {
        setClubsList([]);
      }
    } catch (error) {
      console.error("Error loading clubs:", error);
      setClubsList([]);
    } finally {
      setLoadingClubs(false);
    }
  };

  // Load data with current pagination, search and filter
  const loadData = async () => {
    try {
      const response = await fetchList();
      if (response) {
        setUserList(response.data || []);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    loadData();
    loadClubs();
  }, [currentPage, pageSize]);

  // Handle search and filter - triggers when debounced values change
  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [debouncedSearchTerm, statusFilter, userTypeFilter]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Phone change handler - alag karta hai country code aur mobile number
  const handlePhoneChange = (value, country) => {
    // PhoneInput gives full number with country code
    // Extract country code and mobile number
    const countryCode = `+${country.dialCode}`;
    const mobileNumber = value.substring(country.dialCode.length);

    setFormData((prev) => ({
      ...prev,
      mobile_number: mobileNumber, // Sirf number store karo
      country_code: countryCode, // Country code alag store karo
    }));

    if (formErrors.mobile_number) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.mobile_number;
        return newErrors;
      });
    }
  };

  // Frontend validation
  const validateForm = () => {
    const errors = {};
    if (!formData.full_name || formData.full_name.trim() === "") {
      errors.full_name = t("userManager.nameRequired");
    } else if (formData.full_name.trim().length > 55) {
      errors.full_name = t("userManager.nameMaxLength");
    }

    if (!formData.email || formData.email.trim() === "") {
      errors.email = t("userManager.emailRequired");
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = t("userManager.emailInvalid");
      } else if (formData.email.length > 100) {
        errors.email = t("userManager.emailInvalid");
      }
    }

    if (!formData.mobile_number || formData.mobile_number.trim() === "") {
      errors.mobile_number = t("userManager.mobileInvalid");
    }

    if (!formData.country_code) {
      errors.country_code = t("userManager.countryCodeRequired");
    }

    if (!formData.user_type) {
      errors.user_type = t("userManager.userTypeRequired");
    }

    if (
      CLUB_REQUIRED_USER_TYPES.includes(formData.user_type) &&
      !formData.club
    ) {
      errors.club = t("userManager.clubRequiredForType");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      full_name: "",
      email: "",
      mobile_number: "",
      country_code: "+31",
      user_type: "INDIVIDUAL",
      is_active: true,
      club: "",
    });
    setFormErrors({});
    setSelectedUser(null);
    setIsEditMode(false);
    setIsViewMode(false);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setUserTypeFilter("");
  };

  // Create user - API ko country_code aur mobile_number alag bhejein
  const handleCreate = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    const apiData = {
      full_name: formData.full_name,
      email: formData.email,
      mobile_number: formData.mobile_number,
      country_code: formData.country_code,
      user_type: formData.user_type,
      is_active: formData.is_active,
    };

    if (
      CLUB_REQUIRED_USER_TYPES.includes(formData.user_type) &&
      formData.club
    ) {
      apiData.club = formData.club;
    }

    try {
      const response = await post(apiData);

      if (response?.status === true) {
        Toast.success(response?.message || t("userManager.createdSuccess"));
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) {
          setFormErrors(response.errors);
        } else {
          Toast.error(response?.message || t("userManager.failedCreate"));
        }
      }
    } catch (error) {
      console.error("Error creating user:", error);
      if (error?.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error?.response?.data?.message) {
        Toast.error(error.response.data.message);
      } else {
        Toast.error(error?.message || t("userManager.failedCreate"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!selectedUser?.id) {
      Toast.error(t("userManager.failedUpdate"));
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    const apiData = {
      full_name: formData.full_name,
      email: formData.email,
      mobile_number: formData.mobile_number,
      country_code: formData.country_code,
      is_active: formData.is_active,
    };

    if (
      CLUB_REQUIRED_USER_TYPES.includes(formData.user_type) &&
      formData.club
    ) {
      apiData.club = formData.club;
    }

    try {
      const endpoint = `${API_ENDPOINTS.USERS_UPDATE}/${selectedUser.id}`;
      const response = await putData(endpoint, apiData);

      if (response?.status === true) {
        Toast.success(response?.message || t("userManager.updatedSuccess"));
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) {
          setFormErrors(response.errors);
        } else {
          Toast.error(response?.message || t("userManager.failedUpdate"));
        }
      }
    } catch (error) {
      console.error("Error updating user:", error);
      if (error?.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error?.response?.data?.message) {
        Toast.error(error.response.data.message);
      } else {
        Toast.error(error?.message || t("userManager.failedUpdate"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const newStatus = !item.is_active;
      const endpoint = `${API_ENDPOINTS.USERS_UPDATE}/${item.id}`;

      const response = await patchData(endpoint, {
        is_active: newStatus,
      });

      if (response?.status === true) {
        Toast.success(
          response?.message ||
            (newStatus ? t("common.active") : t("common.inactive")),
        );
        await loadData();
      } else {
        Toast.error(response?.message || t("common.failedUpdateStatus"));
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Toast.error(error.message || t("common.failedUpdateStatus"));
    }
  };

  const openDeleteModal = (item) => {
    setUserToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      const endpoint = `${API_ENDPOINTS.USERS_UPDATE}/${userToDelete.id}`;
      const response = await deleteData(endpoint);

      if (response?.status === true) {
        Toast.success(response?.message || t("userManager.deletedSuccess"));
        await loadData();
      } else {
        Toast.error(response?.message || t("userManager.failedDelete"));
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      Toast.error(error.message || t("userManager.failedDelete"));
    } finally {
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setIsEditMode(false);
    setIsViewMode(false);
    setIsModalOpen(true);
  };

  // Edit modal - API se country_code aur mobile_number alag karke form mein set karo
  const openEditModal = async (item) => {
    try {
      const endpoint = `${API_ENDPOINTS.USERS_UPDATE}/${item.id}`;
      const response = await getById(endpoint);

      if (response?.status === true && response.data) {
        setSelectedUser(response.data);

        let mobileNumber = "";
        let countryCode = "+31"; // Default

        if (response.data.mobile_number) {
          const mobileFull = response.data.mobile_number;
          // Extract country code and mobile number
          if (mobileFull.startsWith("+")) {
            const match = mobileFull.match(/^(\+\d{1,3})(.*)/);
            if (match) {
              countryCode = match[1];
              mobileNumber = match[2];
            } else {
              mobileNumber = mobileFull;
            }
          } else {
            mobileNumber = mobileFull;
          }
        }

        // Agar API se country_code mil raha hai to use karo, nahi to extracted code use karo
        const finalCountryCode = response.data.country_code || countryCode;

        setFormData({
          full_name: response.data.full_name,
          email: response.data.email,
          mobile_number: mobileNumber, // Sirf number store karo
          country_code: finalCountryCode, // Country code alag store karo
          user_type: response.data.user_type,
          is_active: response.data.is_active,
          club: response.data.club || "",
        });
        setFormErrors({});
        setIsEditMode(true);
        setIsViewMode(false);
        setIsModalOpen(true);
      } else {
        Toast.error(t("userManager.failedFetchDetails"));
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      Toast.error(error.message || t("userManager.failedFetchDetails"));
    }
  };

  const getClubName = (clubId) => {
    if (!clubId) return "N/A";
    const club = clubsList.find((c) => c.id === clubId);
    return club ? club.name : "N/A";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUserTypeLabel = (userType) => {
    const option = USER_TYPE_OPTIONS.find((opt) => opt.value === userType);
    return option ? option.label : userType;
  };

  // Table ke liye mobile number display - country_code + mobile_number
  const formatMobileDisplay = (record) => {
    if (record.country_code && record.mobile_number) {
      return `${record.country_code} ${record.mobile_number}`;
    } else if (record.mobile_number) {
      return record.mobile_number;
    }
    return "N/A";
  };

  const phoneInputStyle = {
    width: "100%",
    height: "40px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    paddingLeft: "48px",
  };

  // Table columns
  const columns = [
    {
      title: t("userManager.fullName"),
      dataIndex: "full_name",
      key: "full_name",
      render: (t) => t || "N/A",
    },
    {
      title: t("userManager.emailAddress"),
      dataIndex: "email",
      key: "email",
      render: (t) => t || "N/A",
    },
    {
      title: t("userManager.mobile"),
      key: "mobile",
      render: (_, record) => formatMobileDisplay(record), // Country code + number show karo
    },
    {
      title: t("userManager.userType"),
      dataIndex: "user_type",
      key: "user_type",
      render: (t) => (
        <span className="user-type-badge">{getUserTypeLabel(t)}</span>
      ),
    },
    // {
    //   title: t("userManager.club"),
    //   dataIndex: "club",
    //   key: "club",
    //   render: (clubId, record) => {
    //     if (CLUB_REQUIRED_USER_TYPES.includes(record.user_type)) {
    //       return getClubName(clubId);
    //     }
    //     return "-";
    //   },
    // },
    {
      title: t("userManager.club"),
      dataIndex: "club_name",
      key: "club_name",
      render: (t) => t || "N/A",
      // render: (clubId, record) => {
      //   if (CLUB_REQUIRED_USER_TYPES.includes(record.user_type)) {
      //     return getClubName(clubId);
      //   }
      //   return "-";
      // },
    },
    {
      title: t("userManager.createdAt"),
      dataIndex: "created_at",
      key: "created_at",
      render: (t) => formatDate(t),
    },
    {
      title: t("common.status"),
      dataIndex: "is_active",
      key: "status",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "default"}>
          {isActive ? t("common.active") : t("common.inactive")}
        </Tag>
      ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip
            title={record.is_active ? t("common.inactive") : t("common.active")}
          >
            <Button
              size="small"
              danger={!!record.is_active}
              type={record.is_active ? "default" : "primary"}
              icon={<PoweroffOutlined />}
              onClick={() => handleToggleActive(record)}
            />
          </Tooltip>
          <Tooltip title={t("common.view")}>
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/users/details/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title={t("common.edit")}>
            <Button
              type="primary"
              size="small"
              style={{ background: "#52c41a", borderColor: "#52c41a" }}
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => openDeleteModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const hasFilters = searchTerm || statusFilter || userTypeFilter;

  return (
    <>
      <Card
        title={t("userManager.title")}
        extra={
          <Button type="primary" onClick={openCreateModal}>
            {t("userManager.createUser")}
          </Button>
        }
      >
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <Input
            placeholder={t("userManager.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{ maxWidth: 400 }}
          />
          <Select
            placeholder={t("userManager.filterByUserType")}
            value={userTypeFilter || undefined}
            onChange={(value) => {
              setUserTypeFilter(value || "");
            }}
            options={USER_TYPE_OPTIONS}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder={t("common.filterByStatus")}
            value={statusFilter || undefined}
            onChange={(value) => {
              setStatusFilter(value || "");
            }}
            options={STATUS_OPTIONS}
            style={{ width: 200 }}
            allowClear
          />
          {hasFilters && (
            <Button onClick={resetFilters}>
              {t("common.clearSearchToReorder")}
            </Button>
          )}
        </div>

        <div className="table-responsive">
          <Table
            dataSource={userList}
            columns={columns}
            rowKey="id"
            locale={{
              emptyText: hasFilters
                ? t("userManager.noMatchingUsers")
                : t("userManager.noUsersFound"),
            }}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalCount,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50", "100"],
              showTotal: (total) =>
                `${t("common.showing")} ${(currentPage - 1) * pageSize + 1} ${t("common.to")} ${Math.min(currentPage * pageSize, total)} ${t("common.of")} ${total} ${t("common.entries")}`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              },
            }}
          />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={
            isEditMode
              ? t("userManager.editUser")
              : t("userManager.createNewUser")
          }
          onConfirm={isEditMode ? handleUpdate : handleCreate}
          confirmText={isEditMode ? t("common.update") : t("common.create")}
          confirmDisabled={submitting}
          showConfirm={true}
        >
          <form onSubmit={isEditMode ? handleUpdate : handleCreate}>
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                {t("userManager.fullName")}{" "}
                <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                placeholder={t("userManager.enterFullName")}
                maxLength="55"
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: formErrors.full_name
                    ? "1px solid #dc3545"
                    : "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
              {formErrors.full_name && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {formErrors.full_name}
                </div>
              )}
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                {t("userManager.emailAddress")}{" "}
                <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t("userManager.enterEmail")}
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: formErrors.email
                    ? "1px solid #dc3545"
                    : "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
              {formErrors.email && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {formErrors.email}
                </div>
              )}
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                {t("userManager.mobile")}{" "}
                <span style={{ color: "red" }}>*</span>
              </label>
              <PhoneInput
                country={"nl"}
                value={formData.country_code + formData.mobile_number}
                onChange={handlePhoneChange}
                inputProps={{
                  name: "mobile_number",
                  id: "mobile_number",
                  required: true,
                }}
                inputStyle={{
                  ...phoneInputStyle,
                  border: formErrors.mobile_number
                    ? "1px solid #dc3545"
                    : "1px solid #d1d5db",
                }}
                containerStyle={{ width: "100%" }}
                buttonStyle={{
                  border: formErrors.mobile_number
                    ? "1px solid #dc3545"
                    : "1px solid #d1d5db",
                  borderRight: "none",
                  borderTopLeftRadius: "6px",
                  borderBottomLeftRadius: "6px",
                  background: "#f9fafb",
                }}
                dropdownStyle={{
                  width: "300px",
                  borderRadius: "6px",
                }}
                enableSearch={true}
                searchPlaceholder={t("common.search")}
                placeholder={t("userManager.enterMobileNumber")}
                disableSearchIcon={false}
                countryCodeEditable={false}
              />
              {formErrors.mobile_number && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {formErrors.mobile_number}
                </div>
              )}
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                {t("userManager.userType")}{" "}
                <span style={{ color: "red" }}>*</span>
              </label>
              <select
                name="user_type"
                value={formData.user_type}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: formErrors.user_type
                    ? "1px solid #dc3545"
                    : "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                {USER_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {formErrors.user_type && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {formErrors.user_type}
                </div>
              )}
            </div>

            {/* Club Selection - Only show for specific user types */}
            {CLUB_REQUIRED_USER_TYPES.includes(formData.user_type) && (
              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  {t("userManager.club")}{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <Select
                  placeholder={t("userManager.selectClub")}
                  value={formData.club || undefined}
                  onChange={(value) => handleSelectChange("club", value)}
                  options={clubsList.map((club) => ({
                    value: club.id,
                    label: club.name,
                  }))}
                  style={{ width: "100%" }}
                  disabled={loadingClubs}
                  loading={loadingClubs}
                  showSearch
                  optionFilterProp="label"
                  allowClear={false}
                />
                {formErrors.club && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    {formErrors.club}
                  </div>
                )}
              </div>
            )}
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && userToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setUserToDelete(null);
          }}
          title={t("userManager.deleteUser")}
          onConfirm={handleDeleteConfirm}
          confirmText={t("common.delete")}
          confirmColor="danger"
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "40px", marginBottom: "10px" }}>⚠️</div>
            <h3 style={{ marginBottom: "15px" }}>
              {t("userManager.confirmDelete")}
            </h3>
            <div
              style={{
                textAlign: "left",
                backgroundColor: "#f8f9fa",
                padding: "15px",
                borderRadius: "6px",
                marginBottom: "15px",
              }}
            >
              <p>
                <strong>{t("userManager.fullName")}:</strong>{" "}
                {userToDelete.full_name}
              </p>
              <p>
                <strong>{t("userManager.emailAddress")}:</strong>{" "}
                {userToDelete.email || "N/A"}
              </p>
              <p>
                <strong>{t("userManager.mobile")}:</strong>{" "}
                {formatMobileDisplay(userToDelete)}
              </p>
              <p>
                <strong>{t("userManager.userType")}:</strong>{" "}
                {getUserTypeLabel(userToDelete.user_type)}
              </p>
              {CLUB_REQUIRED_USER_TYPES.includes(userToDelete.user_type) && (
                <p>
                  <strong>{t("userManager.club")}:</strong>{" "}
                  {getClubName(userToDelete.club)}
                </p>
              )}
              <p>
                <strong>{t("userManager.createdAt")}:</strong>{" "}
                {formatDate(userToDelete.created_at)}
              </p>
              <p>
                <strong>{t("common.status")}:</strong>
                <span
                  className={`status-badge ${
                    userToDelete.is_active ? "active" : "inactive"
                  }`}
                  style={{ marginLeft: "10px" }}
                >
                  {userToDelete.is_active
                    ? t("common.active")
                    : t("common.inactive")}
                </span>
              </p>
            </div>
            <p style={{ color: "#dc3545" }}>{t("userManager.deleteWarning")}</p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default UserManager;
